import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { auth, rtdb, db } from "../firebase";
import { ref as rtdbRef, onValue, update as rtdbUpdate, remove, query, orderByChild, equalTo } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { startVoiceCall, endCall as endCallerCall, toggleAudio, toggleVideo, switchCamera } from "../webrtc/voiceCall";
import { answerIncomingCall, rejectCall, endCall as endReceiverCall } from "../webrtc/voiceReceiver";
import VoiceCallOverlay from "../webrtc/VoiceCallOverlay";

const CallContext = createContext();

export function useCall() {
    return useContext(CallContext);
}

export function CallProvider({ children }) {
    const [incomingCall, setIncomingCall] = useState(null);
    const [inCall, setInCall] = useState(false);
    const [callStream, setCallStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [isCaller, setIsCaller] = useState(false);
    const [activeConvId, setActiveConvId] = useState(null); // Track Active Conversation ID
    const [callStatus, setCallStatus] = useState("idle"); // 'idle' | 'ended' | 'rejected'

    const lastEndedTimeRef = useRef(0); // Timestamp to dedupe alerts
    const dialingTimeoutRef = useRef(null); // 🆕 30s Dialing Timeout
    const currentUid = auth.currentUser?.uid;

    // 🧹 Centralized Cleanup Timer
    // Whenever status becomes 'ended' or 'rejected', wait 2s, then reset everything.
    useEffect(() => {
        let timer = null;
        if (callStatus === 'ended' || callStatus === 'rejected') {
            timer = setTimeout(() => {
                resetCallState();
            }, 2000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [callStatus]);

    // 🎧 Global Listener for Incoming Calls (Optimized)
    useEffect(() => {
        if (!currentUid) return;
        const callsRef = rtdbRef(rtdb, "calls");
        const myCallsQuery = query(callsRef, orderByChild("receiver"), equalTo(currentUid));

        // Listen for new calls and sync status
        const unsubscribe = onValue(myCallsQuery, async (snapshot) => {
            const data = snapshot.val() || {};
            let foundActiveCallId = null;

            // 1. Scan for active incoming call for ME
            for (const [convId, callData] of Object.entries(data)) {
                if (callData.status === "dialing" || callData.status === "ringing") {
                    foundActiveCallId = convId;

                    // Logic for New Incoming Call (Async Fetch)
                    if (inCall) continue; // Busy

                    // FETCH USERNAME (Optimized to run only if we don't have this call yet)
                    let callerName = "Unknown Caller";
                    try {
                        const userDoc = await getDoc(doc(db, "users", callData.caller));
                        if (userDoc.exists()) {
                            callerName = userDoc.data().username || userDoc.data().email || "Unknown";
                        }
                    } catch (error) {
                        console.error("Error fetching caller name:", error);
                        callerName = "ChatIQ User";
                    }

                    setIncomingCall(prev => {
                        if (prev && prev.convId === convId) return prev; // No change
                        return {
                            convId,
                            caller: callerName,
                            type: callData.isVideo ? "video" : "voice"
                        };
                    });
                } else if (callData.status === "connected" && inCall) {
                    // Critical: If status is connected and we are inCall, this IS the active call.
                    foundActiveCallId = convId;
                }
            }

            // 2. Sync / Cleanup: If we have an incoming call that is NO LONGER valid/dialing
            setIncomingCall(prev => {
                // If we have a previous incoming call request...
                if (prev) {
                    // And we didn't find it in the "active" check above...
                    if (prev.convId !== foundActiveCallId) {
                        // Check if the specific call data *still exists* in our snapshot?
                        // If it's missing from "data", it means it's gone or receiver changed (unlikely).

                        const specificCallData = data[prev.convId];

                        // If it transitioned to connected/answered/ringing, DO NOT END IT. 
                        if (specificCallData && (specificCallData.status === 'connected' || specificCallData.status === 'answered' || specificCallData.status === 'ringing')) {
                            return null; // Just clear incoming prompt
                        }

                        if (inCall) return null;

                        if (Date.now() - lastEndedTimeRef.current > 4000) {
                            setCallStatus("ended");
                        }
                        return null;
                    }
                }
                return prev;
            });
        });

        return () => unsubscribe();
    }, [currentUid, inCall]);

    // 🆕 30s Auto-Reject for Incoming Calls & Send Ringing Status
    useEffect(() => {
        if (incomingCall) {
            // 1. Notify Caller we are Ringing
            const callRef = rtdbRef(rtdb, `calls/${incomingCall.convId}`);
            rtdbUpdate(callRef, { status: 'ringing' }).catch(err => console.warn("Failed to send ringing status", err));

            // 2. Timeout
            const timer = setTimeout(() => {
                setIncomingCall(null);
                setCallStatus("ended");
            }, 30000);
            return () => clearTimeout(timer);
        }
    }, [incomingCall]);

    // 📡 Monitor Outgoing Call Status (Dialing -> Ringing -> Connected)
    useEffect(() => {
        if (!activeConvId || !isCaller) return;

        const statusRef = rtdbRef(rtdb, `calls/${activeConvId}/status`);
        const unsubscribe = onValue(statusRef, (snapshot) => {
            const status = snapshot.val();
            if (status) {
                if (status === 'ringing') {
                    setCallStatus('ringing');
                } else if (status === 'connected') {
                    setCallStatus('connected');
                } else if (status === 'ended' || status === 'rejected') {
                    handleCallEnded(status.toUpperCase());
                }
            }
        });

        return () => unsubscribe();
    }, [activeConvId, isCaller]);


    // 🛑 Generic Handler when call disconnects via WebRTC signaling
    const handleCallEnded = (reason) => {
        if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);

        // Anti-Duplicate Logic:
        // If we recently ended this call, ignore.
        const now = Date.now();
        if (now - lastEndedTimeRef.current < 4000) {
            return;
        }
        lastEndedTimeRef.current = now;

        setCallStatus(prev => {
            // If already ended/rejected, do nothing
            if (prev === 'ended' || prev === 'rejected') return prev;
            return reason === "REJECTED" ? "rejected" : "ended";
        });

        setCallStream(null);
        setRemoteStream(null);
    };

    const resetCallState = () => {
        setInCall(false);
        setCallStream(null);
        setRemoteStream(null);
        setActiveConvId(null);
        setIncomingCall(null);
        setCallStatus("idle");
        if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);
    };

    // 📞 Start Voice Call
    const initiateVoiceCall = async (convId, recipientUid) => {
        // No explicit remove - overwriting with set is safe and atomic to prevent race conditions


        setIsCaller(true);
        setActiveConvId(convId);
        setCallStatus("dialing"); // Set initial status
        setInCall(true); // Show UI immediately
        lastEndedTimeRef.current = 0; // Reset debounce

        // 30s Timeout: If no answer
        dialingTimeoutRef.current = setTimeout(() => {
            terminateCall();
        }, 30000);

        try {
            const stream = await startVoiceCall(
                convId,
                currentUid,
                recipientUid,
                false, // Audio Only
                (remote) => {
                    // Call Answered!
                    if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);
                    setRemoteStream(new MediaStream(remote.getTracks()));
                },
                (reason) => handleCallEnded(reason)
            );
            setCallStream(stream);

            // 🔔 Notify Recipient (Custom Server)
            // 🔔 Notify Recipient
            // Notification is handled automatically by the Cloud Function (sendCallNotification)
            // when the call data is written to the Realtime Database.


        } catch (err) {
            if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);
            alert("Failed to start voice call.");
            console.error(err);
            resetCallState();
        }
    };

    // 🎥 Start Video Call
    const initiateVideoCall = async (convId, recipientUid) => {
        // No explicit remove


        setIsCaller(true);
        setActiveConvId(convId);
        setCallStatus("dialing"); // Set initial status
        setInCall(true); // Show UI immediately
        lastEndedTimeRef.current = 0;

        // 30s Timeout
        dialingTimeoutRef.current = setTimeout(() => {
            terminateCall();
        }, 30000);

        try {
            const stream = await startVoiceCall(
                convId,
                currentUid,
                recipientUid,
                true, // Video
                (remote) => {
                    // Call Answered!
                    if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);
                    setRemoteStream(new MediaStream(remote.getTracks()));
                },
                (reason) => handleCallEnded(reason)
            );
            setCallStream(stream);

            // 🔔 Notify Recipient (Custom Server)
            // 🔔 Notify Recipient
            // Notification is handled automatically by the Cloud Function (sendCallNotification)
            // when the call data is written to the Realtime Database.

        } catch (err) {
            if (dialingTimeoutRef.current) clearTimeout(dialingTimeoutRef.current);
            alert("Failed to start video call.");
            console.error(err);
            resetCallState();
        }
    };

    // ✅ Accept Call
    const acceptCall = async () => {
        if (!incomingCall) return;
        const convId = incomingCall.convId;

        setIsCaller(false);
        setActiveConvId(convId);
        lastEndedTimeRef.current = 0;

        try {
            const result = await answerIncomingCall(
                convId,
                currentUid,
                (local) => setCallStream(local),
                (remote) => setRemoteStream(new MediaStream(remote.getTracks())),
                (reason) => handleCallEnded(reason)
            );

            if (result === "MISSING") {
                setIncomingCall(null);
                setCallStatus("ended");
                return;
            }
            if (result === "MEDIA_ERROR") {
                alert("Could not access Camera or Microphone.");
                setIncomingCall(null);
                return;
            }

            setInCall(true);
            setIncomingCall(null);
        } catch (err) {
            console.error("Error accepting call:", err);
            alert("Failed to connect.");
            resetCallState();
        }
    };

    // ❌ Reject Incoming Call
    const declineCall = () => {
        if (!incomingCall?.convId) return;
        rejectCall(incomingCall.convId);
        setIncomingCall(null);
        setCallStatus("rejected");
    };

    // 📴 Hang Up Active Call
    const terminateCall = () => {
        const targetConvId = activeConvId || incomingCall?.convId;
        if (!targetConvId) return;

        // Anti-Duplicate: Mark as ended locally first
        lastEndedTimeRef.current = Date.now();

        if (isCaller) {
            endCallerCall(targetConvId);
        } else {
            endReceiverCall(targetConvId);
        }

        setCallStatus("ended");
    };

    // --- Media Controls ---
    const toggleMyAudio = (enabled) => {
        toggleAudio(enabled);
    };

    const toggleMyVideo = (enabled) => {
        toggleVideo(enabled);
    };

    const switchMyCamera = async () => {
        const newStream = await switchCamera();
        if (newStream) {
            // Force re-render by creating new MediaStream reference
            setCallStream(new MediaStream(newStream.getTracks()));
        }
    }

    return (
        <CallContext.Provider value={{ initiateVoiceCall, initiateVideoCall, toggleMyAudio, toggleMyVideo, switchMyCamera }}>
            {children}

            {/* 🔔 Incoming Call Overlay */}
            {incomingCall && (
                <VoiceCallOverlay
                    type="incoming"
                    callerId={incomingCall.caller}
                    callType={incomingCall.type}
                    onAccept={acceptCall}
                    onReject={declineCall}
                />
            )}

            {/* 📞 Active Call Overlay */}
            {inCall && (
                <VoiceCallOverlay
                    type="ongoing"
                    chatId={activeConvId} // Added
                    localStream={callStream}
                    remoteStream={remoteStream}
                    onEnd={terminateCall}
                    onToggleAudio={toggleMyAudio}
                    onToggleVideo={toggleMyVideo}
                    onSwitchCamera={switchMyCamera}
                    callStatus={callStatus}
                />
            )}

            {/* 🛑 Status Card Overlay (Independent of Active/Incoming state) */}
            {(callStatus === 'ended' || callStatus === 'rejected') && !inCall && (
                <VoiceCallOverlay
                    callStatus={callStatus}
                />
            )}
        </CallContext.Provider>
    );
}
